import { useCallback, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import GameView from './GameView.jsx'
import HoopsTabs from './HoopsTabs.jsx'
import TonightView from './TonightView.jsx'
import { validGameId } from './api.js'
import { LOADING, READY, useClock, useHoopsBoxes, useHoopsFantasy, useHoopsTonight } from './poll.js'

// The Hoops pill's panel. P6-328b (GN-1b), and P6-330b (GN-3b) for the fun layer.
//
// The page a few friends open on a phone to watch NBA games together in Discord: tonight's board, and
// one game when you tap into it. The bot side is live (GN-1a publishes `3a/hoops/`, and GN-3a the data
// for the fun layer), the design is `docs/design-briefs/hoops/`, and this is its FULL BUILD short of
// fantasy — the cards, the no-games night and the game view, with the win-probability strip, the latest
// callout and the five on the floor on a live game, and the shot chart and the flow in the game view.
// Fantasy is GN-6's and none of it is drawn here. And nothing is drawn as an empty place for what a game
// does not have: no strip in preseason, no shot chart for a game with no shots.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 AN UNREADABLE FEED IS THE HEADER AND NO CARDS, WITH NO ERROR COPY
// ═══════════════════════════════════════════════════════════════════════════
//
// HP5, and the presence handoff's unreachable rule: this page may say what it last knew and how old
// that is, and it may not say a feed is broken, because it does not know that. So:
//
//   never read, and refused     the header (`Hoops`, `Tonight`) and the docs link. No sentence.
//   read once, since refused    the LAST COPY, whose cards age on their own clocks and go stale on
//                               their own (`model.js`), which is the honest rendering of a feed that
//                               has stopped being heard.
//
// ⚠️ NOT EVEN "LOADING" IS WRITTEN FOR LONG: it is one muted line while the first request is in
// flight, and it vanishes into either the board or nothing.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE GAME IS A HISTORY ENTRY, NOT A ROUTE — AND NOT A PIECE OF STATE THE BACK BUTTON CANNOT SEE
// ═══════════════════════════════════════════════════════════════════════════
//
// Opening a game is a screen change on a phone, and the phone's own Back gesture is how a person
// leaves it. Local React state would send that gesture out of the portal entirely. So opening a game
// PUSHES a history entry carrying `{ hoopsGame: <id> }` in the router's location state, and Back pops
// it: the Tonight view returns, the portal does not. No URL changes, which is deliberate:
//
//   * a new route would need a tier-3 entry in `content-src/sections.json` for the guard, the nav
//     generator and a dozen gates to learn about, for a screen whose only entrance is a card;
//   * the portal's pill selection is not in the URL either (a refresh lands on movie night), so a
//     deep link into the middle of Hoops would have to teach `PortalHome` to open the pill first.
//
// ⚠️ `history.state` SURVIVES A RELOAD, so a reload while a game is open and a return to the Hoops pill
// reopens that game. That is the entry the browser is on, and it is the right answer.
//
// ⚠️ "← TONIGHT" GOES BACK WHEN THIS PANEL PUSHED THE ENTRY and replaces it when it did not (the entry
// came from before this mount): `navigate(-1)` from an entry this panel did not create would leave
// the portal, which is the exact thing the design is avoiding.
//
// 🔴 `docPath` IS DRAWN IN EVERY STATE. A docs page does not depend on `/api/hoops/*`, and
// `doc-links.test.jsx` is the gate that says every pill's panel links to its page.

const STATE_KEY = 'hoopsGame'

/** The location this panel is on, as a path (its pathname, search and hash). */
function here(location) {
  return `${location.pathname}${location.search}${location.hash}`
}

/** Which game is open, and how to open and close one. */
function useGameSelection() {
  const location = useLocation()
  const navigate = useNavigate()
  const pushed = useRef(false)
  const selected = validGameId(location.state?.[STATE_KEY])

  const open = useCallback(
    (gameId) => {
      pushed.current = true
      navigate(here(location), { state: { ...(location.state ?? {}), [STATE_KEY]: gameId } })
    },
    [location, navigate],
  )

  const close = useCallback(() => {
    if (pushed.current) {
      pushed.current = false
      navigate(-1)
      return
    }
    const { [STATE_KEY]: _closed, ...rest } = location.state ?? {}
    navigate(here(location), { replace: true, state: Object.keys(rest).length > 0 ? rest : null })
  }, [location, navigate])

  return { selected, open, close }
}

export default function HoopsPanel({ docPath, timeZone }) {
  const board = useHoopsTonight()
  const nowMs = useClock()
  // P6-333a (GN-6a): the member's OWN league copy, or `null`. `null` is every way of having none (not in a league, not read
  // yet, refused), and then the page draws nothing fantasy-shaped and reads no extra box score (GN2).
  const blob = useHoopsFantasy()
  const boxes = useHoopsBoxes(board.tonight?.games ?? [], blob !== null)
  const fantasy = blob === null ? null : { blob, boxes }
  const { selected, open, close } = useGameSelection()

  // A game from last night (P6-343) is a card the board holds under `last_night` and opens like any other.
  const card =
    selected === null
      ? null
      : [...(board.tonight?.games ?? []), ...(board.tonight?.last_night?.games ?? [])].find((game) => game.id === selected) ?? null

  return (
    <div className="d2d-hoops" data-testid="hoops-panel">
      {/* P6-329c: the tab bar is `HoopsTabs.jsx`'s, shared with the season views. Tonight is the screen this is, so it is
          the button it always was (it closes an open game and does nothing otherwise); the four beside it are links to
          `/hoops/players`, `/hoops/leaders`, `/hoops/standings` and `/hoops/teams`. */}
      <HoopsTabs current="tonight" tonightIsHere onTonight={selected === null ? undefined : close} />

      {selected !== null ? (
        <GameView
          key={selected}
          gameId={selected}
          card={card}
          fantasy={fantasy}
          teams={board.teams}
          nowMs={nowMs}
          timeZone={timeZone}
          onBack={close}
        />
      ) : board.state === READY ? (
        <TonightView
          tonight={board.tonight}
          fantasy={fantasy}
          teams={board.teams}
          nowMs={nowMs}
          timeZone={timeZone}
          onOpen={open}
        />
      ) : board.state === LOADING ? (
        <p className="d2d-portal__flat" data-testid="hoops-loading">
          Loading tonight’s games…
        </p>
      ) : null}

      {docPath ? (
        <Link className="d2d-portal__more d2d-micro" to={docPath}>
          How hoops works →
        </Link>
      ) : null}
    </div>
  )
}
